/**
 * CohortRetentionHeatmap.tsx — Cohort retention colour-coded HTML table
 *
 * Purpose:
 *   Renders cohort retention data as a styled HTML table. Each cell's
 *   background colour is mapped via Tailwind utility classes to the
 *   retention percentage bucket. No third-party heatmap library is used.
 *
 * @module packages/billing-admin/src/components/revenue/CohortRetentionHeatmap
 * @since 2026-06-03 (AT-50.03 — Revenue Dashboard & Enterprise Screens)
 */
import React from 'react';
import type { CohortRetention } from '../../hooks/useAdminApi50';

/** Month column keys displayed in the heatmap (month0 = acquisition month). */
const MONTH_KEYS = [
  'month0',
  'month1',
  'month2',
  'month3',
  'month4',
  'month5',
  'month6',
] as const;

type MonthKey = typeof MONTH_KEYS[number];

/** Map a retention percentage (0–100) to Tailwind background + text classes. */
function retentionColor(value: number): string {
  if (value >= 90) return 'bg-green-700 text-white';
  if (value >= 75) return 'bg-green-500 text-white';
  if (value >= 60) return 'bg-green-300 text-gray-900';
  if (value >= 45) return 'bg-yellow-300 text-gray-900';
  if (value >= 30) return 'bg-orange-300 text-gray-900';
  if (value > 0) return 'bg-red-300 text-gray-900';
  return 'bg-gray-100 text-gray-400';
}

interface CohortRetentionHeatmapProps {
  /** Array of cohort rows from GET /admin/reports/cohorts. */
  data: CohortRetention[];
}

/**
 * CohortRetentionHeatmap — colour-coded retention table.
 *
 * @since 2026-06-03 (AT-50.03)
 */
export default function CohortRetentionHeatmap({ data }: CohortRetentionHeatmapProps) {
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr>
            <th className="text-left px-2 py-1 font-medium text-gray-600 whitespace-nowrap">Cohort</th>
            {MONTH_KEYS.map((k, i) => (
              <th key={String(k)} className="px-2 py-1 font-medium text-gray-600 text-center whitespace-nowrap">
                M+{i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={row.cohort}>
              <td className="px-2 py-1 font-medium text-gray-700 whitespace-nowrap">{row.cohort}</td>
              {MONTH_KEYS.map(k => {
                const val = row[k as MonthKey] as number;
                return (
                  <td
                    key={String(k)}
                    className={`px-2 py-1 text-center rounded ${retentionColor(val)}`}
                    title={`${val}%`}
                  >
                    {val > 0 ? `${val}%` : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
